import React, { useCallback, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Grid,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { useAppTranslation, usePagination } from 'src/admin/core/hooks'
import { useAgencies } from '../hooks'
import { generateFilterGraphql } from 'src/admin/core'
import { getAppBarState } from 'src/store'
import { Agency } from 'src/core/models'
import { DataGridColumns } from 'src/admin/core/components/DataGrid/CustomDataGrid'
import { AdminDataGrid } from 'src/admin/core/components/DataGrid/AdminDataGrid'
import { ContentDataGrid } from 'src/admin/core/components/DataGrid'
import { GridRowParams } from '@mui/x-data-grid'

export interface AgencySearchDrawerProps {
  open: boolean
  onClose: () => void
  onSelect: (agency: Agency) => void
}

export const AgencySearchDrawer: React.FC<AgencySearchDrawerProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const { label } = useAppTranslation()
  const { results, loading, getAgencies } = useAgencies()
  const { page, pageSize, skip, handlePageChange, handlePageSizeChange } = usePagination()
  const [searchTerm, setSearchTerm] = useState('')
  const { userEnterprise } = getAppBarState().appState

  const getCustomFilters = useCallback((search: string) => {
    return {
      and: [
        {
          or: [
            { name: { ...generateFilterGraphql(search, 'contains') } },
            { name: { ...generateFilterGraphql(search.toUpperCase(), 'contains') } },
            { name: { ...generateFilterGraphql(search.toLowerCase(), 'contains') } },
            {
              name: {
                ...generateFilterGraphql(
                  search.charAt(0).toUpperCase() + search.slice(1).toLowerCase(),
                  'contains',
                ),
              },
            },
          ],
        },
        { name: { ...generateFilterGraphql('ONLINE_AGENCY', 'neq') } },
        { enterpriseId: { ...generateFilterGraphql(userEnterprise.id, 'eq') } },
      ],
    }
  }, [userEnterprise.id])

  const handleSearch = useCallback(() => {
    if (searchTerm.length >= 3) {
      const customFilters = getCustomFilters(searchTerm)
      getAgencies({ where: customFilters, skip: 0, take: pageSize })
    }
  }, [searchTerm, getCustomFilters, getAgencies, pageSize])

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && searchTerm.length >= 3) {
      handleSearch()
    }
  }

  // Reset search when drawer opens
  useEffect(() => {
    if (open) {
      setSearchTerm('')
    }
  }, [open])

  // Handle pagination changes
  useEffect(() => {
    if (searchTerm.length >= 3) {
      const customFilters = getCustomFilters(searchTerm)
      getAgencies({ where: customFilters, skip: skip, take: pageSize })
    }
  }, [skip, pageSize])

  const getColumnDefinitions = useCallback((): DataGridColumns => {
    return {
      NOMBRE: { field: 'name', contentType: 'string', align: 'left' },
      TIPO: { field: 'type', contentType: 'string', align: 'left' },
      ESTADO: { field: 'active', contentType: 'boolean', headerAlign: 'left', align: 'left' },
    }
  }, [])

  const handleRowClick = (row: GridRowParams) => {
    const selectedAgency = results.results.find((agency) => agency.id === row.id)
    if (selectedAgency) {
      onSelect(selectedAgency)
      onClose()
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '500px', md: '600px' },
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {label('BUSQUEDA_DE_AGENCIA')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search Section */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={label('ESCRIBA_EL_NOMBRE_DE_LA_AGECIA')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              helperText={searchTerm.length > 0 && searchTerm.length < 3 ? label('MINIMO_3_CARACTERES') : ''}
              error={searchTerm.length > 0 && searchTerm.length < 3}
              InputProps={{
                sx: { backgroundColor: 'background.paper' },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={searchTerm.length < 3}
              sx={{ minWidth: '120px', height: '40px' }}
            >
              {label('BUSCAR')}
            </Button>
          </Box>
        </Box>

        {/* Results Table */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <ContentDataGrid pageSize={pageSize}>
            <AdminDataGrid
              columns={getColumnDefinitions()}
              data={results.results}
              onRowClick={handleRowClick}
              count={results.metadata.Count}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              page={page}
              pageSize={pageSize}
            />
          </ContentDataGrid>
        </Box>
      </Box>
    </Drawer>
  )
}
